import React from "react";
import { Box, Typography, TextField, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ConfigProvider, Select } from "antd";
import SingleDatePicker from "../../../Helper/DatePicker/SingleDatePicker";
import RadioOptionGroup from "../../../Components/RadioOptionGroup";
import PillCheckbox from "../../../Components/PillCheckbox";
import {
    BandLabel,
    FieldLabel,
    INCOMING_BAND_COLOR,
    OUTGOING_BAND_COLOR,
} from "./PackCardShared";
import CircleArrowRightIcon from "../../../Components/Icons/CircleArrowRightIcon";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import SerialSearchField from "./SerialSearchField";
import { DateFormatString } from "../../../Helper/DatePicker/DateFormatters";
import { getLocalDateFromUTC, getDatePickerDate } from "../../../Helper/DatePicker/DateConverter";
import {
    MIN_SERIAL_SEARCH_LENGTH,
    SERIAL_LOOKUP_DEBOUNCE_MS,
    formatPackChipLabel,
    getSerialFieldMessage,
    getIncomingSerialConflict,
    formatSerialInstalledElsewhere,
    getObservationOptionsForWarranty,
    getPackRegistryConfig,
    isRegistryDateLocked,
    isTypedSerialInvalid,
    matchesAutoFilledSerial,
    toCalendarDay,
    toTypedSerial,
} from "../recordReplacementHelper";
import {
    getBatteryPackBySerial,
    getPackSerialSuggestions,
} from "../../../Api/BatteryPackApi";

// "New" condition is only valid for Type 1; Type 2/3 allow Refurbished /
// Cannibalised only.
const NEW_ONLY_GRADE = "TYPE_1";

const antdTheme = {
    token: { colorPrimary: "#6D72F6", borderRadius: 8, fontFamily: "Roboto" },
    components: {
        Select: {
            controlHeight: 40,
            optionSelectedBg: "#EEF0FF",
            optionSelectedColor: "#6D72F6",
            optionSelectedFontWeight: 500,
        },
    },
};

// Auto-filled (read-only) serial input: grey background, readable dark text.
const serialFilledSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        fontSize: "14px",
        backgroundColor: "#F5F5F5",
    },
    "& .MuiOutlinedInput-input": { fontSize: "14px" },
    "& .MuiOutlinedInput-root.Mui-disabled": {
        backgroundColor: "#F5F5F5",
        "& fieldset": { borderColor: "#E4E4E4" },
    },
    "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "#494949" },
    "& .MuiFormHelperText-root": { marginLeft: 0, marginTop: "5px", fontSize: "12px" },
    "& .MuiFormHelperText-root.Mui-error": { color: "#F04438" },
};

// One pack's OUTGOING -> INCOMING swap, as a compact two-band card.
//
// ALL of the serial-field machinery below (registry auto-fill of the manufacturing
// date, its read-only lock, the out-of-order lookup guard, the unmatched-serial
// warning) is state of THIS component instance, not of the page. That is what makes
// it per-pack: a request can hold six of these cards, and holding any of this above
// the card would let one card's serial lookup overwrite or unlock another's date.
export default function PackReplacementCard({
    pack,
    config = {},
    batteryID,
    errors = {},
    canRemove = false,
    onChangeOutgoing = () => {},
    onChangeIncoming = () => {},
    onRemove = () => {},
    cardRef = null,
}) {
    const { warrantyOptions = [], gradeTypes = [], conditionOptions = [] } = config;
    const { warnWhenUnregistered, suggestionLimit } = getPackRegistryConfig(config);

    const outgoing = pack?.outgoing || {};
    const incoming = pack?.incoming || {};

    // Suggestion handlers, one per mode — they are NOT interchangeable.
    //   incoming: every registered serial, taken ones included -- the form refuses a
    //             taken one on selection rather than hiding it (see
    //             getIncomingSerialConflict)
    //   outgoing: serials free anywhere OR already on THIS battery — the incoming filter
    //             would hide the very serial the technician needs whenever the pack in the
    //             slot is correctly recorded.
    // Signature and return shape match what DeviceSearchInput expects from
    // searchSuggestionsHandler: (search, columnName, limit) -> the raw GetData envelope.
    const incomingSuggestionsHandler = React.useCallback(
        (search, columnName, limit) =>
            getPackSerialSuggestions(batteryID, { mode: "incoming", search, limit }),
        [batteryID]
    );

    const outgoingSuggestionsHandler = React.useCallback(
        (search, columnName, limit) =>
            getPackSerialSuggestions(batteryID, { mode: "outgoing", search, limit }),
        [batteryID]
    );

    // The unmatched-serial warning is inferred from the last suggestion fetch — the
    // platform search box already fetches them, so no extra request is needed. Keyed by
    // the term it was fetched for, otherwise the warning flashes while a newer fetch is in
    // flight.
    //
    // This holds for OUTGOING only. Outgoing suggestions exempt this battery, so the
    // serial in the slot is listed and absence really does mean unregistered.
    const [serialMatches, setSerialMatches] = React.useState({});

    // INCOMING cannot use the list. It offers only FREE serials, so absence covers two
    // opposite situations -- a serial nobody has heard of, and one already installed --
    // and calling both "unmatched, please verify" points the technician at the wrong
    // problem. Only the registry lookup separates them.
    const [incomingLookup, setIncomingLookup] = React.useState(null);

    const trackSuggestions = (field) => (suggestions, searchTerm) => {
        const matched = (suggestions || []).includes(searchTerm);
        setSerialMatches((prev) => ({
            ...prev,
            [field]: { term: searchTerm, matched },
        }));

        // Runs whether or not the term was listed. Gating this on a match was right
        // only while incoming suggestions included taken serials; now that they offer
        // only free ones, the UNLISTED case is precisely the one worth resolving --
        // it is where an installed-elsewhere conflict shows up. Without it the
        // technician gets "unmatched, please verify" for a serial the registry knows
        // perfectly well, and no signal until the backend 409.
        // Debounced here, in the caller. DeviceSearchInput has no debounce of its own
        // -- its effect runs on every distinct searchTerm -- so dropping the `matched`
        // gate would otherwise add one registry read per keystroke on top of the
        // suggestions read the box already makes.
        if (field === "incoming") {
            if (lookupTimer.current) clearTimeout(lookupTimer.current);
            if (
                (searchTerm || "").length >= MIN_SERIAL_SEARCH_LENGTH &&
                searchTerm !== autoFilledDateSerial
            ) {
                lookupTimer.current = setTimeout(
                    () => applyIncomingSerialDetail(searchTerm),
                    SERIAL_LOOKUP_DEBOUNCE_MS
                );
            }
        }
    };

    const isSerialUnmatched = (field, currentValue) => {
        if (!warnWhenUnregistered) return false;
        const trimmed = (currentValue || "").trim();
        // Below the search floor the backend returns [] by design, not "no such serial", so
        // warning here would flag every serial as unregistered after the first keystroke.
        if (trimmed.length < MIN_SERIAL_SEARCH_LENGTH) return false;
        if (field === "incoming") {
            // Known-but-taken is not unmatched: it gets the installed-elsewhere
            // message, which is the one the technician can act on.
            return !!incomingLookup && incomingLookup.term === trimmed
                ? !incomingLookup.found
                : false;
        }
        const state = serialMatches[field];
        return !!state && state.term === trimmed && !state.matched;
    };

    // Guards the async setState below. A technician can pick a serial and immediately
    // navigate away, cancel the wizard, or remove this very card; without this the lookup
    // resolves into an unmounted component.
    const isMounted = React.useRef(true);
    const lookupTimer = React.useRef(null);
    React.useEffect(
        () => () => {
            isMounted.current = false;
            if (lookupTimer.current) clearTimeout(lookupTimer.current);
        },
        []
    );

    // The serial whose registry date currently sits in the manufacturing-date field, or
    // null when the date was typed by hand (or is empty). Locking the field is keyed on
    // this rather than a plain boolean so the lock releases the moment the serial it
    // belongs to is edited.
    const [autoFilledDateSerial, setAutoFilledDateSerial] = React.useState(null);

    // Last serial a registry lookup was fired for. Two quick picks can resolve out of
    // order; without this the first response would overwrite the second pick's date.
    const pendingSerialLookup = React.useRef(null);

    // Picking a registered serial fills the manufacturing date from the registry. Second
    // call by design: the suggestions endpoint returns a flat string array so it can stay
    // shared with the inventory table search. Leaves the field empty and editable when the
    // registry has no date on record, so the technician can still enter it.
    const applyIncomingSerialDetail = async (serial) => {
        // Already asked, or asking, about this exact serial. Selecting a suggestion
        // both calls this directly AND changes the search box's value, which re-fires
        // its suggestions effect -- without this, one click cost two identical reads.
        if (pendingSerialLookup.current === serial) return;
        pendingSerialLookup.current = serial;
        const res = await getBatteryPackBySerial(serial);
        if (!isMounted.current || pendingSerialLookup.current !== serial) return;
        // A failed request is not evidence. Recording "not found" here would report
        // every serial as unregistered whenever the endpoint is down.
        if (res?.responseStatus?.code !== 200) return;
        const rows = res.response.data || [];
        const row = rows[0] || {};
        setIncomingLookup({ term: serial, found: rows.length > 0 });

        // The same row answers both questions, so one lookup covers them: what the
        // registry knows this pack's manufacturing date to be, and whether it is
        // currently sitting in another bus.
        const patch = { installedElsewhere: getIncomingSerialConflict(row, batteryID) };

        const manufacturingDate = toCalendarDay(row.manufacturingDate);
        if (manufacturingDate) {
            patch.manufacturingDate = manufacturingDate;
            setAutoFilledDateSerial(serial);
        }
        onChangeIncoming(patch);
    };

    // A registry date is a fact about one specific pack, so it travels with its serial:
    // editing the serial clears the date and unlocks the field. A hand-typed date is left
    // alone — the technician entered it deliberately and nothing here knows better.
    const releaseAutoFilledDate = (nextSerial) => {
        if (!autoFilledDateSerial) return {};
        if (matchesAutoFilledSerial(autoFilledDateSerial, nextSerial)) return {};
        pendingSerialLookup.current = null;
        // The lookup result is a fact about one serial, so it dies with it -- a stale
        // "found" would keep the unmatched advisory silent for a serial nobody asked
        // the registry about.
        setIncomingLookup(null);
        setAutoFilledDateSerial(null);
        return { manufacturingDate: null };
    };

    const handleIncomingSerialChange = (rawSerial) => {
        const serial = toTypedSerial(rawSerial);
        // Unconditionally, NOT inside releaseAutoFilledDate -- that early-returns when
        // no date was auto-filled, leaving an in-flight read for the previous serial
        // free to land and write a conflict for a serial no longer typed.
        pendingSerialLookup.current = null;
        // installedElsewhere is a fact about one specific serial, so it is dropped the
        // moment the serial changes -- otherwise a stale conflict would keep blocking a
        // serial the technician has already corrected.
        onChangeIncoming({
            serial,
            // Typed by hand, so the format rule applies from here on. Set on every
            // keystroke rather than once, because a technician can pick a suggestion and
            // then keep typing -- which makes the value hand-made again.
            serialTyped: true,
            installedElsewhere: null,
            ...releaseAutoFilledDate(serial),
        });
    };

    const handleIncomingSerialSelect = (serial) => {
        if (!serial) return;
        onChangeIncoming({
            serial,
            // Came from the registry, so it is exempt: an already-recorded serial that
            // predates this rule must stay selectable.
            serialTyped: false,
            installedElsewhere: null,
            ...releaseAutoFilledDate(serial),
        });
        applyIncomingSerialDetail(serial);
    };

    // "Installed on" is read-only only when a date was already ON RECORD when the form
    // opened. Keying it on the serial prefill alone left the field locked at "-" for a
    // slot whose serial is known but whose install date was never captured; keying it on
    // the current value would re-lock the field the instant a date was picked.
    const hasRecordedInstalledOn = !!pack?.installedOnOnRecord;

    const isManufacturingDateAutoFilled = isRegistryDateLocked(
        autoFilledDateSerial,
        incoming.serial,
        incoming.manufacturingDate
    );

    // Options follow THIS pack's warranty answer, so an observation can never
    // contradict the warranty it is recorded against. Empty until warranty is
    // answered, which keeps the block hidden until then.
    const observationOptions = getObservationOptionsForWarranty(
        config,
        outgoing.warrantyStatus
    );

    const toggleObservation = (code) => {
        const current = outgoing.observations || [];
        onChangeOutgoing({
            observations: current.includes(code)
                ? current.filter((c) => c !== code)
                : [...current, code],
        });
    };

    // Changing warranty invalidates any observation picked against the previous
    // answer, since the two lists are different vocabularies.
    const handleWarrantyChange = (warrantyStatus) =>
        onChangeOutgoing({ warrantyStatus, observations: [] });

    const handleGradeChange = (value) => {
        const patch = { grade: value };
        if (value !== NEW_ONLY_GRADE && incoming.condition === "NEW") {
            patch.condition = "";
        }
        onChangeIncoming(patch);
    };

    // "New" is offered only for Type 1 — disabled rather than hidden, so the row does not
    // reflow when the grade changes and the reason can be explained on hover.
    const conditionChoices = (conditionOptions || []).map((opt) => ({
        code: opt.code,
        label: opt.label,
        disabled: opt.code === "NEW" && incoming.grade !== NEW_ONLY_GRADE,
        disabledReason:
            opt.code === "NEW" && incoming.grade !== NEW_ONLY_GRADE
                ? "A new pack is only valid for grade Type 1"
                : undefined,
    }));

    // An error value may be `true` (missing) or a message string (e.g. a serial
    // duplicated across this request) — show the message when there is one.
    // What each serial field says, and whether it reads as an error or an advisory.
    // The precedence lives in the helper so it is unit-tested rather than repeated
    // in two JSX ternaries (which is how it came to be wrong).

    // Only a hand-typed serial is format-checked. `serialTyped` is false for the slot
    // prefill and for anything picked from the dropdown, which is what keeps an
    // already-recorded serial -- including one carrying a homoglyph from the original
    // import -- from blocking a replacement the technician has no other way to record.
    const isSerialFormatInvalid = (side) => {
        const sideState = side === "outgoing" ? outgoing : incoming;
        return !!sideState.serialTyped && isTypedSerialInvalid(sideState.serial);
    };

    const serialMessage = (side, serial, fallback) =>
        getSerialFieldMessage({
            error: errors[`${side}Serial`],
            isInvalidFormat: isSerialFormatInvalid(side),
            isUnmatched: isSerialUnmatched(side, serial),
            fallback,
        });

    const outgoingMessage = serialMessage(
        "outgoing",
        outgoing.serial,
        "Search or type the serial number"
    );
    const incomingMessage = getSerialFieldMessage({
        // Shown as soon as the lookup resolves rather than waiting for a submit: the
        // technician is choosing a pack right now and this is the moment it is useful.
        // validateRecordReplacement raises the same message from form state, so submit
        // is blocked too.
        error:
            errors.incomingSerial ||
            (incoming.installedElsewhere
                ? formatSerialInstalledElsewhere(incoming.installedElsewhere.vehicleLabel)
                : null),
        isInvalidFormat: isSerialFormatInvalid("incoming"),
        isUnmatched: isSerialUnmatched("incoming", incoming.serial),
        fallback: " ",
    });
    // The read-only input is filled from the slot itself, so the unmatched advisory
    // cannot apply: there is nothing the technician typed for them to verify. An
    // error still shows, since a duplicated outgoing serial is reachable whenever two
    // slots each have one on record.
    const outgoingFilledMessage = getSerialFieldMessage({
        error: errors.outgoingSerial,
        fallback: " ",
    });

    return (
        <Box
            // The page holds these so it can scroll a just-added pack into view.
            // Taken as a plain prop rather than through forwardRef: the page only
            // needs the DOM node, and a wrapper element would join the parent's
            // flex layout as an extra item.
            ref={cardRef}
            sx={{
                border: "1px solid #EAEAEA",
                borderRadius: "12px",
                backgroundColor: "#fff",
                overflow: "hidden",
            }}
        >
            {/* ---- Card header: which slot this card is for ---- */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    backgroundColor: "#F9F9FC",
                    borderBottom: "1px solid #EFEFEF",
                    padding: "12px 18px",
                }}
            >
                <Box
                    data-testid={`pack-chip-${pack?.key}`}
                    sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#4A4AF4",
                        backgroundColor: "#EEF0FF",
                        borderRadius: "6px",
                        padding: "5px 12px",
                    }}
                >
                    {formatPackChipLabel(pack)}
                </Box>

                {canRemove && (
                    <PrimaryTooltip title="Remove this pack from the request" placement="top">
                        <IconButton
                            size="small"
                            data-testid={`remove-pack-${pack?.key}`}
                            onClick={() => onRemove(pack)}
                            sx={{ color: "#8D8D8D", "&:hover": { color: "#E5484D" } }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </PrimaryTooltip>
                )}
            </Box>

            <Box sx={{ position: "relative", padding: "16px 18px 18px" }}>
                {/* ---- OUTGOING ---- */}
                <Box
                    sx={{
                        border: "1px solid #ECDCA0",
                        backgroundColor: "#FFFBEA",
                        borderRadius: "10px",
                        padding: "0 18px 16px",
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "132px minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.7fr)",
                        },
                        columnGap: "20px",
                        alignItems: "start",
                    }}
                >
                    <BandLabel text="OUTGOING" color={OUTGOING_BAND_COLOR} />

                    <Box sx={{ pt: "14px" }}>
                        <FieldLabel required>Current pack serial number</FieldLabel>
                        {/* When packConfig holds this slot's serial the field is auto-filled and
                            read-only, so there is nothing to search — keep the plain input. The
                            autocomplete only applies to the not-recorded case, which is exactly
                            where the technician previously had to type blind. */}
                        {pack?.serialAutoFilled ? (
                            <TextField
                                fullWidth
                                size="small"
                                value={outgoing.serial || ""}
                                disabled
                                error={outgoingFilledMessage.isError}
                                sx={serialFilledSx}
                                inputProps={{ "data-testid": `outgoing-serial-${pack?.key}` }}
                                helperText={outgoingFilledMessage.text}
                            />
                        ) : (
                            <SerialSearchField
                                value={outgoing.serial || ""}
                                onValueChange={(serial) =>
                                    onChangeOutgoing({
                                        serial: toTypedSerial(serial),
                                        serialTyped: true,
                                    })
                                }
                                onSelect={(serial) =>
                                    serial &&
                                    onChangeOutgoing({ serial, serialTyped: false })
                                }
                                searchSuggestionsHandler={outgoingSuggestionsHandler}
                                suggestionLimit={suggestionLimit}
                                error={outgoingMessage.isError}
                                testId={`outgoing-serial-${pack?.key}`}
                                placeholder="Search or type the serial number"
                                onSuggestionsChange={trackSuggestions("outgoing")}
                                isWarning={outgoingMessage.isWarning}
                                helperText={outgoingMessage.text}
                            />
                        )}
                    </Box>

                    <Box sx={{ pt: "14px" }}>
                        <FieldLabel>Installed on</FieldLabel>
                        {hasRecordedInstalledOn ? (
                            <Box
                                sx={{
                                    backgroundColor: "#F5F5F5",
                                    border: "1px solid #E4E4E4",
                                    borderRadius: "8px",
                                    padding: "9px 12px",
                                    fontSize: "14px",
                                    color: "#494949",
                                }}
                            >
                                {/* installedOn is a backend UTC datetime; show the
                                    user-zone calendar day so it matches the review. This
                                    branch only renders when a date is on record, so there
                                    is no empty case to fall back to. */}
                                {DateFormatString(
                                    getLocalDateFromUTC(outgoing.installedOn, "YYYY-MM-DD")
                                )}
                            </Box>
                        ) : (
                            // Nothing on record => let the user capture the install date.
                            <SingleDatePicker
                                value={outgoing.installedOn || null}
                                handleOnChange={(date) =>
                                    onChangeOutgoing({
                                        installedOn: date ? date.format("YYYY-MM-DD") : null,
                                    })
                                }
                                // A pack cannot have been installed in the future. Must
                                // be the ORG's today (getDatePickerDate reads the
                                // timeZone cookie), not the browser's: a support user in
                                // another zone would otherwise have the org's real today
                                // greyed out, and an IST technician working after
                                // midnight could pick a day the backend's UTC guard then
                                // rejects with a generic error.
                                maxDate={getDatePickerDate()}
                                placeholder="Select date"
                                format="YYYY-MM-DD"
                                singleIcon
                                fullWidth
                            />
                        )}
                    </Box>

                    <Box sx={{ pt: "14px" }}>
                        <FieldLabel required>Warranty</FieldLabel>
                        <RadioOptionGroup
                            options={warrantyOptions}
                            value={outgoing.warrantyStatus || ""}
                            onChange={handleWarrantyChange}
                            error={!!errors.warranty}
                            ariaLabel="warranty status"
                            testIdPrefix={`warranty-${pack?.key}`}
                        />
                    </Box>

                    {/* Spans the band's full width beneath the fields: the option
                        labels are sentences, not values, so they do not fit a column. */}
                    {observationOptions.length > 0 && (
                        <Box sx={{ gridColumn: { md: "1 / -1" }, pt: "10px" }}>
                            <FieldLabel required>
                                {outgoing.warrantyStatus === "IN"
                                    ? "Observation for In-warranty"
                                    : "Observation for out of warranty"}
                            </FieldLabel>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                    columnGap: "16px",
                                    rowGap: "2px",
                                }}
                            >
                                {observationOptions.map((option) => (
                                    <PillCheckbox
                                        key={option.code}
                                        testId={`observation-${pack?.key}-${option.code}`}
                                        checked={(outgoing.observations || []).includes(option.code)}
                                        onChange={() => toggleObservation(option.code)}
                                        label={option.label}
                                        labelSize={14}
                                    />
                                ))}
                            </Box>
                            {errors.observations && (
                                <Typography sx={{ fontSize: "12px", color: "#E5484D", mt: "4px" }}>
                                    Please select at least one observation
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {/* ---- Arrow, straddling the two bands ---- */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        // Pull the two bands together and float the arrow over the seam.
                        my: "-14px",
                        position: "relative",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                >
                    <CircleArrowRightIcon direction="down" size={34} />
                </Box>

                {/* ---- INCOMING ---- */}
                <Box
                    sx={{
                        border: "1px solid #BEE0C7",
                        backgroundColor: "#EDF7EF",
                        borderRadius: "10px",
                        padding: "0 18px 16px",
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "132px minmax(0, 1.7fr) minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.9fr)",
                        },
                        columnGap: "20px",
                        alignItems: "start",
                    }}
                >
                    <BandLabel text="INCOMING" color={INCOMING_BAND_COLOR} />

                    <Box sx={{ pt: "14px" }}>
                        <FieldLabel required>New pack serial number</FieldLabel>
                        <SerialSearchField
                            value={incoming.serial || ""}
                            onValueChange={handleIncomingSerialChange}
                            onSelect={handleIncomingSerialSelect}
                            searchSuggestionsHandler={incomingSuggestionsHandler}
                            suggestionLimit={suggestionLimit}
                            error={incomingMessage.isError}
                            testId={`incoming-serial-${pack?.key}`}
                            placeholder="New serial"
                            onSuggestionsChange={trackSuggestions("incoming")}
                            isWarning={incomingMessage.isWarning}
                            helperText={incomingMessage.text}
                        />
                    </Box>

                    <Box sx={{ pt: "14px" }}>
                        <FieldLabel required>MFG date</FieldLabel>
                        {/* Controlled, not defaultValue: the registry auto-fill sets this
                            from outside the picker, and an uncontrolled picker would keep
                            showing "Select date" while form state already held the date. */}
                        <SingleDatePicker
                            value={incoming.manufacturingDate || null}
                            handleOnChange={(date) =>
                                onChangeIncoming({
                                    manufacturingDate: date ? date.format("YYYY-MM-DD") : null,
                                })
                            }
                            // A pack cannot have been manufactured in the future.
                            // Org's today, not the browser's — same reasoning as the
                            // install-date cap above.
                            maxDate={getDatePickerDate()}
                            placeholder="Select date"
                            format="YYYY-MM-DD"
                            singleIcon
                            fullWidth
                            status={errors.manufacturingDate ? "error" : ""}
                            // Read-only while it holds the registry's answer for this
                            // serial — editing it would silently disagree with the record.
                            disabled={isManufacturingDateAutoFilled}
                        />
                        <Typography sx={{ fontSize: "12px", mt: "5px", minHeight: "16px", color: errors.manufacturingDate ? "#E5484D" : "#6B6B6B" }}>
                            {errors.manufacturingDate
                                ? "Please select MFG date"
                                : isManufacturingDateAutoFilled
                                ? "Auto-filled from the pack registry"
                                : " "}
                        </Typography>
                    </Box>

                    <Box sx={{ pt: "14px" }}>
                        <FieldLabel required>Grade</FieldLabel>
                        <ConfigProvider theme={antdTheme}>
                            <Select
                                data-testid={`grade-select-${pack?.key}`}
                                value={incoming.grade || undefined}
                                onChange={handleGradeChange}
                                placeholder="Select"
                                style={{ width: "100%", fontSize: "14px" }}
                                status={errors.grade ? "error" : ""}
                                options={gradeTypes.map((g) => ({ value: g.code, label: g.label }))}
                            />
                        </ConfigProvider>
                    </Box>

                    <Box sx={{ pt: "14px" }}>
                        <FieldLabel required>Condition</FieldLabel>
                        <RadioOptionGroup
                            options={conditionChoices}
                            value={incoming.condition || ""}
                            onChange={(condition) => onChangeIncoming({ condition })}
                            error={!!errors.condition}
                            ariaLabel="incoming pack condition"
                            testIdPrefix={`condition-${pack?.key}`}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
